import React, { memo, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, frames } from "../../../Styles";
import { Location } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";
import { currentLfViewTypes, useLfContext } from "../Modals/_Modals/lfPenContext";
import { ProgressBar } from "../..";
import { BarTypes } from "../../ProgressViewer/progressBar";
import { AnimalInfo, useAttentionLog } from "../../../Services";

interface PenBasic_Props {
  pen: Location;
  gridArea: string
  focus_view?: boolean;
}

export default function LF_PEN({ pen, gridArea, focus_view }: PenBasic_Props) {
  const { t } = useTranslation();
  const { state: { selectedPens, currentPenViewType, penContent, connection, domain, systemId }, dispatch } = useLfContext();
  
  // #region | Attentiontype
  const { data: attentionLogState, call: getAttentionLogs } = useAttentionLog(connection);

  useEffect(() => {
    if (systemId) {
      getAttentionLogs({ ids: [systemId] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const logData = useMemo(() => {
    if (domain in attentionLogState && attentionLogState[domain] && typeof attentionLogState[domain] === "object" && "data" in attentionLogState[domain] && attentionLogState[domain].data && systemId) {
      return attentionLogState[domain].data.filter((log) => log.fk_systems === systemId && log.dismissed !== true);
    }
    return []
  }, [attentionLogState, domain, systemId])

  const hasAttention = useMemo(() => {
    return logData.some(data => data.fk_locations === pen.location_id);
  }, [logData, pen.location_id]);
  
  const penAttentionLevel = useMemo(() => logData.find(data => data.fk_locations === pen.location_id)?.level, [logData, pen.location_id])
  // #endregion
  
  // #region | SystemControle
  // On pen click
  const handleClick = () => {
    const alreadySelected = selectedPens?.some((p) => p === pen);

    let newSelectedList;

    if (alreadySelected) {
      newSelectedList = selectedPens?.filter((p) => p !== pen);
    } else {
      newSelectedList = [...(selectedPens ?? []), pen];
    }

    dispatch({ selectedPens: newSelectedList });
  }
  // #endregion

  // #region | Pen Content
  const thisAnimal = useMemo(() => {
    if (!pen) return null;
    return pen.animals.find(a => a.location_id === pen.location_id)
  }, [pen]) 

  const _penContent = useMemo(() => penContent === 0
    ? thisAnimal
      ? `${thisAnimal.animal_number}`
      : t("locationFeederPages.sections.noAnimal", {defaultValue: "No Animal"})
  : penContent === 1
    ? pen
      ? `${pen.name}`
      : t("noData", {defaultValue: "No Data"})
  : undefined, [pen, penContent, t, thisAnimal]);
  
  const StyledPenContent = ({attention=false}: {attention?: boolean}) => ( <frames.LfPen $gridArea={gridArea}>
    { (!thisAnimal && penContent === 0) || (!penContent && penContent === 1)
    ? <p style={{ ...css.emptyPen, opacity: "0.5" }}>
        {_penContent}
      </p>
      : <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>{_penContent}</p>}
      {attention ? <Attention attentionLevel={penAttentionLevel} hasAttention={hasAttention} /> : <></>}
  </frames.LfPen>)
  // #endregion

  const Pen = () => {
    switch (currentPenViewType) {
      case currentLfViewTypes.systemControle:
        return (
          <frames.LfPen
            onClick={handleClick}
            $gridArea={gridArea} $selectable $isSelected={selectedPens && selectedPens.some(p => p === pen)}
            style={{ ...fonts.paragraph, ...(focus_view ? fonts.fontSize3 : fonts.fontSize4), cursor: "pointer" }}
          >
            {pen?.name}
          </frames.LfPen>
        );
      case currentLfViewTypes.attention:
        return (
          <StyledPenContent attention/>
        );
      case currentLfViewTypes.remainingToday:
      case currentLfViewTypes.remainingYesterday:
        return ( thisAnimal
          ? <div style={{gridArea: gridArea}}><Consumption pen={pen} thisAnimal={thisAnimal} currentPenViewType={currentPenViewType} penContent={penContent}/></div>
          : <StyledPenContent/>
        );
      case currentLfViewTypes.basic:
      default:
        return <StyledPenContent/>
    }
  }

  return Pen();
}

// #region | Attention Pen
const Attention = memo(({ attentionLevel, hasAttention }: { attentionLevel?: number, hasAttention: boolean }) => {
  const [animate, setAnimate] = useState<boolean>(false);
  const _width = animate ? "10px" : "0px"
  
  useEffect(() => {
    setAnimate(true);
  }, []);

  if (!hasAttention) return;
  return (
    <div style={{
      top: "0",
      left: "0",
      position: "absolute",
      zIndex: "2",
      height: "100%",
      width: _width,
      backgroundColor: attentionLevel === 0 ? colors.systemRed : colors.systemYellow,
      transition: "width 0.5s ease"
    }} />
  ) 
})
// #endregion

// #region | Consumption Pens
const Consumption = memo(({pen, thisAnimal, penContent, currentPenViewType}:{pen: Location, thisAnimal: AnimalInfo, penContent?: number, currentPenViewType: currentLfViewTypes}) => {
  if (!thisAnimal) return;

  const consumptionToday = thisAnimal.consumptions ? thisAnimal.consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 0):undefined;
  const consumptionTotalToday = +((consumptionToday?.total ?? 0) / 1000).toFixed(2);
  const consumptionProgressedToday = +(consumptionTotalToday - (consumptionToday?.progressed ?? 0) / 1000).toFixed(2);
  const consumptionYesterday = thisAnimal.consumptions ? thisAnimal.consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === 1):undefined;
  const consumptionTotalYesterday = +((consumptionYesterday?.total ?? 0) / 1000).toFixed(2);
  const consumptionProgressedYesterday = +(consumptionTotalYesterday - (consumptionYesterday?.progressed ?? 0) / 1000).toFixed(2);

  return (
    <ProgressBar
      barType={BarTypes.pen}
      animation
      labels={{
        penLabel: penContent === 0 ? thisAnimal.animal_number : pen.name,
        noDataDisclaimer: penContent === 0 ? `${thisAnimal.animal_number}` :  `${pen.name}`
      }}
      total={currentPenViewType === currentLfViewTypes.remainingYesterday ? consumptionTotalYesterday : consumptionTotalToday}
      progressed={currentPenViewType === currentLfViewTypes.remainingYesterday ? consumptionProgressedYesterday : consumptionProgressedToday}
    />
  )
});
// #endregion